// src/components/legal/IngredientCheckerAccountDeletion.js
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Container = styled.div`
    max-width: 800px;
    margin: 0 auto;
    padding: 3rem 2rem;
    line-height: 1.8;
    color: #333;
`;

const Header = styled.header`
    text-align: center;
    margin-bottom: 3rem;
    padding-bottom: 2rem;
    border-bottom: 2px solid #eee;
`;

const Title = styled.h1`
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: #9BB167;
`;

const UpdateDate = styled.p`
    color: #666;
    font-size: 1rem;
`;

const BackLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    color: #9BB167;
    text-decoration: none;
    margin-bottom: 2rem;
    transition: opacity 0.3s;

    &:hover {
        opacity: 0.8;
    }

    &::before {
        content: '\2190';
        margin-right: 0.5rem;
    }
`;

const Section = styled.section`
    margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: #444;
`;

const Paragraph = styled.p`
    margin-bottom: 1rem;
    text-align: justify;
    white-space: pre-wrap;
`;

const WarningBox = styled.div`
    background: #FFEBEE;
    border-left: 4px solid #F44336;
    padding: 1.5rem;
    border-radius: 5px;
    margin: 2rem 0;
`;

const InfoBox = styled.div`
    background: #F0F4E8;
    padding: 1.5rem;
    border-radius: 10px;
    margin: 2rem 0;
    white-space: pre-wrap;
`;

const StepBox = styled.div`
    background: #F5F5F5;
    padding: 1.5rem;
    border-radius: 10px;
    margin: 1rem 0;

    h3 {
        color: #9BB167;
        margin-bottom: 0.5rem;
    }
`;

const ContactBox = styled.div`
    background: #F0F4E8;
    padding: 1.5rem;
    border-radius: 10px;
    margin: 2rem 0;
`;

const IngredientCheckerAccountDeletion = () => {
    return (
        <Container>
            <BackLink to="/ingredientchecker">성분체크로 돌아가기</BackLink>

            <Header>
                <Title>성분체크 계정 삭제 안내</Title>
                <UpdateDate>최종 수정일: 2026년 3월 16일</UpdateDate>
            </Header>

            <WarningBox>
                <strong>경고:</strong> 계정 삭제는 되돌릴 수 없습니다. 삭제 전 중요한 데이터를 백업하시기 바랍니다.
                특히 스캔 기록, AI 성분 분석 결과는 복구가 불가능합니다.
            </WarningBox>

            <Section>
                <SectionTitle>계정 삭제 방법</SectionTitle>

                <StepBox>
                    <h3>방법 1: 앱 내에서 직접 삭제</h3>
                    <Paragraph>
                        1. 성분체크 앱을 실행합니다
                        2. 하단 설정 탭을 선택합니다
                        3. [회원탈퇴]를 선택합니다
                        4. 탈퇴 사유를 선택합니다
                        5. 안내 사항을 확인하고 동의합니다
                        6. [탈퇴하기] 버튼을 탭합니다
                        7. 최종 확인 팝업에서 [탈퇴]를 선택합니다
                    </Paragraph>
                </StepBox>

                <StepBox>
                    <h3>방법 2: 이메일로 요청</h3>
                    <Paragraph>
                        아래 정보를 포함하여 mind-wellness@naver.com으로 이메일을 보내주세요:

                        • 가입 시 사용한 이메일 주소
                        • 사용자명 또는 닉네임
                        • 계정 삭제 요청 사유 (선택사항)
                        • 본인 확인을 위한 추가 정보 (가입일, 최근 이용 내역 등)

                        이메일 제목: [계정 삭제 요청] 사용자명

                        처리 기간: 요청 접수 후 영업일 기준 3-5일
                    </Paragraph>
                </StepBox>
            </Section>

            <Section>
                <SectionTitle>삭제되는 데이터</SectionTitle>
                <Paragraph>
                    계정 삭제 시 다음 데이터가 영구적으로 삭제됩니다:

                    <strong>개인정보:</strong>
                    • 이메일 주소
                    • 프로필 설정
                    • 로그인 정보 및 세션 데이터

                    <strong>스캔 및 분석 데이터:</strong>
                    • 바코드 스캔 기록
                    • AI 성분 분석 결과
                    • 즐겨찾기 제품 목록
                    • 알레르기 및 식품 선호도 설정
                    • 업로드한 제품 이미지

                    <strong>구독 정보:</strong>
                    • 결제 내역
                    • 구독 상태 및 기간
                    • 프리미엄 기능 사용 기록
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>보존되는 데이터</SectionTitle>
                <Paragraph>
                    법적 의무 준수를 위해 일부 데이터는 제한된 기간 동안 보존됩니다:

                    <strong>법적 보존 의무:</strong>
                    • 전자상거래 기록: 5년 (전자상거래법)
                    • 결제 및 환불 기록: 5년 (국세기본법)
                    • 계약 또는 청약철회 기록: 5년
                    • 소비자 불만 또는 분쟁 처리 기록: 3년
                    • 접속 로그: 3개월

                    <strong>익명화 데이터:</strong>
                    • 서비스 개선을 위한 통계 데이터 (개인 식별 불가)
                    • 집계된 사용자 행동 분석 데이터

                    이러한 데이터는 개인을 식별할 수 없는 형태로만 보관됩니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>삭제 전 확인사항</SectionTitle>
                <Paragraph>
                    <strong>계정 삭제 전 반드시 확인하세요:</strong>

                    ✓ 스캔 기록 및 분석 결과를 별도로 백업하셨나요?
                    ✓ 남은 구독 기간이 있나요? (환불 정책 확인)

                    <strong>구독 관련 안내:</strong>
                    • 유료 구독 중인 경우, 먼저 구독을 취소하세요
                    • 남은 구독 기간에 대한 환불은 스토어 정책에 따릅니다
                    • Google Play 구독은 스토어에서 별도 취소가 필요합니다
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>삭제 처리 절차</SectionTitle>
                <Paragraph>
                    <strong>앱 내 삭제:</strong>
                    • 삭제 요청 시 즉시 처리
                    • 모든 개인 데이터 즉시 완전 삭제
                    • 로그인 및 서비스 접근 즉시 차단

                    <strong>이메일 삭제 요청:</strong>
                    • 본인 확인 절차 진행
                    • 삭제 요청 확인 이메일 발송
                    • 본인 확인 완료 후 즉시 삭제 처리
                    • 모든 개인 데이터 완전 제거

                    <strong>처리 기간:</strong>
                    • 앱 내 삭제: 즉시 삭제
                    • 이메일 요청: 본인 확인 후 즉시 삭제 (보통 3-5 영업일 내)
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>삭제 후 영향</SectionTitle>
                <Paragraph>
                    <strong>서비스 이용 불가:</strong>
                    • 모든 성분체크 기능 접근 불가
                    • 스캔 기록 및 AI 성분 분석 결과 복구 불가
                    • 프리미엄 기능 이용 불가

                    <strong>재가입 정책:</strong>
                    • 동일 이메일로 즉시 재가입 가능
                    • 이전 데이터는 복구 불가능
                    • 신규 사용자와 동일하게 처리
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>특별 상황</SectionTitle>
                <Paragraph>
                    <strong>미성년자 계정:</strong>
                    • 만 14세 미만: 법정대리인 동의 필요
                    • 만 14-19세: 법정대리인 요청 시 삭제 가능
                    • 부모 확인 절차 필요

                    <strong>사망자 계정:</strong>
                    • 가족의 요청으로 삭제 가능
                    • 사망 증명서 등 서류 필요
                    • 데이터 백업 요청 가능

                    <strong>법적 보존 명령:</strong>
                    • 수사기관 요청 시 삭제 보류
                    • 법원 명령에 따른 보존
                    • 해당 사용자에게 가능한 한 통지
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>데이터 이동권</SectionTitle>
                <Paragraph>
                    <strong>데이터 내보내기:</strong>
                    계정 삭제 전 모든 개인 데이터를 다운로드할 수 있습니다.

                    <strong>제공 형식:</strong>
                    • JSON 형식 (기계 판독 가능)
                    • CSV 형식 (스캔 기록 데이터)
                    • PDF 형식 (분석 리포트)

                    <strong>포함 내용:</strong>
                    • 프로필 정보
                    • 바코드 스캔 기록 및 AI 분석 결과
                    • 즐겨찾기 목록
                    • 알레르기 및 식품 선호도 설정

                    <strong>요청 방법:</strong>
                    • 이메일: mind-wellness@naver.com
                    • 처리 기간: 최대 30일
                </Paragraph>
            </Section>

            <ContactBox>
                <SectionTitle>고객 지원</SectionTitle>
                <Paragraph>
                    계정 삭제와 관련하여 도움이 필요하신가요?

                    <strong>이메일:</strong> mind-wellness@naver.com
                    <strong>응답 시간:</strong> 영업일 기준 48시간 이내
                    <strong>운영 시간:</strong> 평일 09:00-18:00 (KST)

                    <strong>자주 묻는 질문:</strong>
                    • 삭제 후 같은 이메일로 재가입할 수 있나요? → 네, 가능합니다
                    • 삭제된 데이터를 복구할 수 있나요? → 아니요, 불가능합니다
                    • 구독 환불은 어떻게 받나요? → 스토어 정책에 따라 처리됩니다
                </Paragraph>
            </ContactBox>

            <InfoBox>
                <SectionTitle>법적 권리</SectionTitle>
                <Paragraph>
                    <strong>GDPR (EU 사용자):</strong>
                    • 삭제권 (잊힐 권리)
                    • 데이터 이동권
                    • 처리 제한권

                    <strong>CCPA (캘리포니아 사용자):</strong>
                    • 개인정보 삭제 요청권
                    • 차별받지 않을 권리

                    <strong>한국 개인정보보호법:</strong>
                    • 개인정보 삭제 요구권
                    • 처리 정지 요구권

                    관련 권리 행사: mind-wellness@naver.com
                </Paragraph>
            </InfoBox>

            <InfoBox>
                <SectionTitle>문서 정보</SectionTitle>
                <Paragraph>
                    • 최초 작성일: 2026년 3월 16일
                    • 최종 수정일: 2026년 3월 16일
                    • 버전: 1.0
                    • 다음 검토일: 2026년 9월 16일
                </Paragraph>
            </InfoBox>
        </Container>
    );
};

export default IngredientCheckerAccountDeletion;
